/**
 * weightBalanceAdapters — pure transform utilities for the Month V2 W&B feature.
 *
 * Consumed by sub-epics B/C/D/E. No React, Redux, or imports from
 * `source/js/modules/calendar/weight-and-balance/**` (cyclic-import trap via
 * the `eventDuration` constant re-exported from the connected component).
 */

import { unwrap } from './apiEnvelope.js';

// ─── 1. Response unwrap ───────────────────────────────────────────────────────

/**
 * Strip the Symfony BaseViewResponse envelope from a W&B API response.
 * Delegates to the shared `unwrap` helper used across all calendar-month slices.
 *
 * @param {*} response Raw value returned by CalendarApi.getWeightAndBalance.
 * @returns {*} Unwrapped payload or null.
 */
export function unwrapWeightBalanceResponse(response) {
	return unwrap(response);
}

// ─── 2. Draft extraction ──────────────────────────────────────────────────────

// ─── Taxi & Run-up Fuel Burn sign convention ─────────────────────────────────
//
// The wire value is a POSITIVE magnitude. Verified on dev 2026-09-17: Symfony
// returns 422 Unprocessable Content for any negative `taxiRunUpFuelBurn`
// (both a user-typed "-2" and a client-negated "-2.00"), and accepts "6".
//
// History, because it is easy to get this backwards again: the legacy PHP
// stored the burn NEGATIVE and did `bcadd(taxiRunUpFuelBurn, ramp)`
// (`CalendarForOrganization.php:5865`), with a table input that accepted the
// sign (`DECIMAL_NEGATIVE_PATTERN`). Symfony kept the `add` but added a
// positive-only validator, so today a positive burn RAISES takeoff weight
// (owner report, 2026-09-16: 6 → takeoff = ramp + 6). That subtraction can
// only be fixed on the server; a client-side negation was tried and every
// recalculation 422'd. Until Symfony subtracts, the wire stays positive.
//
// These helpers normalise the sign: the input shows and sends the magnitude,
// and a user who still types "-6" out of legacy habit gets the same result.

export const TAXI_BURN_KEY = 'Taxi & Run-up Fuel Burn';

/** API/table value (any sign) → magnitude for the input. */
export function taxiBurnForDisplay(raw) {
	if (raw === '' || raw == null) return '';
	const n = parseFloat(String(raw).replace(',', '.'));
	if (!Number.isFinite(n)) return String(raw);
	return String(Math.abs(n));
}

/** Input (any sign) → the positive 2-dp string Symfony's validator accepts. */
export function taxiBurnForApi(display) {
	const n = parseFloat(String(display ?? '').replace(',', '.'));
	if (!Number.isFinite(n) || n === 0) return '0.00';
	return Math.abs(n).toFixed(2);
}

// ─── Editable-field display formatting ───────────────────────────────────────
//
// Trims trailing zeros for an editable W&B input so backend values like
// `2.0000000000` display as `2` without breaking precision on intentional
// decimals (e.g. `1473.50` → `1473.5`, `2.5` → `2.5`).
//
// Apply this ONCE, at the point a value enters a field from outside the
// keyboard (a loaded/recalculated/saved payload, or a copy/auto-fill
// shortcut) — never on every render/keystroke, or a user typing "1." /
// "1.0" gets it silently collapsed back to "1" before they can finish typing
// "1.05".

/** Server/table value → trimmed display string for an editable input. */
export function cleanNumDisplay(value) {
	if (value === '' || value == null) return '';
	const n = parseFloat(String(value).replace(',', '.'));
	if (isNaN(n)) return String(value);
	return parseFloat(n.toFixed(10)).toString();
}

/**
 * Extract the editable W&B draft fields from the raw API payload.
 * Mirrors the shape consumed by the legacy `weight-and-balance.js` component.
 *
 * @param {Object} payload Unwrapped API payload (result of unwrapWeightBalanceResponse).
 * @returns {{
 *   passengers: Array,
 *   rows: Array,
 *   unit: string|null,
 *   fuelLoad: string|null,
 *   taxiRunUpFuelBurn: string|null,
 *   eventDuration: string|null,
 *   fuelWeightTable: Object|null,
 *   weightAndBalanceTable: Object,
 *   image: string|null,
 * }}
 */
export function toWeightBalanceDraft(payload) {
	return {
		passengers: payload?.passengers ?? [],
		rows: payload?.rows ?? [],
		unit: payload?.unit ?? null,
		fuelLoad: payload?.fuelLoad ?? null,
		taxiRunUpFuelBurn:
			payload?.taxiRunUpFuelBurn == null ? null : taxiBurnForDisplay(payload.taxiRunUpFuelBurn),
		eventDuration: payload?.eventDuration ?? null,
		fuelWeightTable: payload?.fuelWeightTable ?? null,
		weightAndBalanceTable: payload?.weightAndBalanceTable ?? {},
		image: payload?.image ?? null,
		// Predefined passengers for the quick-select dropdown (legacy parity:
		// `passenger.modal.js:261-292` renders these as "Apply" buttons).
		availablePassengers: payload?.availablePassengers ?? []
	};
}

// ─── 3. Save payload builder ──────────────────────────────────────────────────

/**
 * Convert a W&B draft back into the request body expected by
 * `UpdateWeightAndBalanceRequest.php` (CalendarApi.updateWeightAndBalance /
 * CalendarApi.recalculateWeightAndBalance).
 *
 * Only includes fields that are non-null so the backend can apply partial
 * updates without clobbering omitted fields.
 *
 * @param {{
 *   unit?: string,
 *   fuelLoad?: string|number,
 *   taxiRunUpFuelBurn?: string|number,
 *   eventDuration?: string|number,
 *   rows?: Array<{name: string, weight: string}>,
 *   passengers?: Array,
 * }} draft
 * @returns {Object} Body ready for CalendarApi.updateWeightAndBalance.
 */
export function toUpdateWeightBalancePayload(draft) {
	const payload = {};
	if (draft.unit != null) payload.unit = draft.unit;
	if (draft.fuelLoad != null) payload.fuelLoad = String(draft.fuelLoad);
	if (draft.taxiRunUpFuelBurn != null)
		payload.taxiRunUpFuelBurn = taxiBurnForApi(draft.taxiRunUpFuelBurn);
	if (draft.eventDuration != null) payload.eventDuration = String(draft.eventDuration);
	if (draft.rows != null) payload.rows = draft.rows;
	if (draft.passengers != null) payload.passengers = draft.passengers;
	return payload;
}

// ─── 4. Table → display rows ──────────────────────────────────────────────────

/**
 * Convert a raw `weightAndBalanceTable` object into an ordered array of
 * display-ready row descriptors.
 *
 * Ported from `getWeightAndBalanceFields` (weight-and-balance.helpers.js) and
 * `getStatusCode` (weight-and-balance.js:268-310). The passenger-seat layout
 * logic is intentionally excluded — month-view renders passengers via the
 * legacy PassengerSeats component unchanged.
 *
 * @param {Object} weightAndBalanceTable Raw table from the API payload.
 * @param {string[]} [warning=[]] Global warning messages from the API response.
 * @returns {Array} Row descriptors consumable by B/C/D/E render logic.
 */
export function buildWeightBalanceRows(weightAndBalanceTable, warning = []) {
	if (!weightAndBalanceTable) return [];
	const rows = [];

	Object.keys(weightAndBalanceTable).forEach(key => {
		const item = weightAndBalanceTable[key];

		if (key === 'status' && Array.isArray(item)) {
			rows.push({ status: item });
			return;
		}

		if (Array.isArray(item)) {
			item.forEach(row => {
				rows.push({
					id: row.Name,
					title: row.Name,
					arm: row.Arm,
					moment: row.Moment,
					group: 'row',
					weight: {
						name: row.Name,
						editable: row.Edit,
						value: row.Edit ? cleanNumDisplay(row.Weight) : row.Weight
					}
				});
			});
			return;
		}

		if (typeof item === 'string') {
			rows.push({ id: key, title: key, code: item });
			return;
		}

		rows.push({
			id: key,
			title: key,
			group: null,
			weight: {
				select: item.Select,
				value:
					key === TAXI_BURN_KEY
						? taxiBurnForDisplay(item.Weight)
						: item.Edit
							? cleanNumDisplay(item.Weight)
							: item.Weight,
				name: key,
				editable: item.Edit
			},
			arm: item.Arm,
			moment: item.Moment,
			status: item.status,
			isTime: item.isTime
		});
	});

	// Merge global warning messages as status entries (mirrors getStatusCode logic)
	if (warning && warning.length) {
		rows.push({ status: warning.map(message => ({ success: false, message })) });
	}

	return rows;
}

// ─── 5. Envelope chart point builders ────────────────────────────────────────

/**
 * Extract Ramp/Takeoff/Landing W&B chart points from the table.
 * Ported from `extractWbPoints` in weight-and-balance.js:172-194.
 *
 * @param {Object} weightAndBalanceTable
 * @returns {Array<{label: string, weight: number, cg: number, status: Object|null}>}
 */
export function buildWbPoints(weightAndBalanceTable) {
	if (!weightAndBalanceTable) return [];
	const keys = [
		{ key: 'Ramp Weight & CG', label: 'Ramp' },
		{ key: 'Takeoff Weight & CG', label: 'Takeoff' },
		{ key: 'Landing Weight & CG', label: 'Landing' }
	];
	const points = [];
	keys.forEach(({ key, label }) => {
		const entry = weightAndBalanceTable[key];
		if (!entry) return;
		const weight = parseFloat(entry.Weight);
		const cg = parseFloat(entry.Arm);
		if (!weight || !cg) return;
		points.push({
			label,
			weight,
			cg,
			status: entry.status && entry.status.length > 0 ? { errors: entry.status } : null
		});
	});
	return points;
}

/**
 * Compute min/max fuel case envelope points for the chart.
 * Ported from `computeFuelCasePoints` in weight-and-balance.js:196-236.
 *
 * @param {Object} weightAndBalanceTable
 * @param {Object} fuelWeightTable `{ max: { lbs, cg }, min: { lbs, cg } }`
 * @returns {Array|null} At least 2 points, or null when data is insufficient.
 */
export function buildFuelCasePoints(weightAndBalanceTable, fuelWeightTable) {
	if (!weightAndBalanceTable || !fuelWeightTable) return null;

	const zeroFuel = weightAndBalanceTable['Zero Fuel Weight & CG'];
	const zeroFuelWeight = parseFloat(zeroFuel?.Weight);
	const zeroFuelMoment = parseFloat(zeroFuel?.Moment);
	if (!zeroFuelWeight || !zeroFuelMoment) return null;

	const maxFuelArm = parseFloat(fuelWeightTable?.max?.cg);
	const currentFuelArm = parseFloat(weightAndBalanceTable['Fuel Weight']?.Arm);
	const maxFuelLbs = parseFloat(fuelWeightTable?.max?.lbs);
	const minFuelLbs = parseFloat(fuelWeightTable?.min?.lbs);
	const fuelBurn = Math.abs(parseFloat(weightAndBalanceTable['Fuel Burn in Flight']?.Weight)) || 0;

	const computePoint = (fuelLbs, fuelArm) => {
		const totalWeight = zeroFuelWeight + fuelLbs;
		const totalMoment = zeroFuelMoment + fuelLbs * fuelArm;
		return { weight: totalWeight, cg: totalMoment / totalWeight };
	};

	const points = [];

	if (maxFuelLbs && maxFuelArm) {
		points.push({ type: 'maxFuelTakeoff', ...computePoint(maxFuelLbs, maxFuelArm) });
		const maxLandingFuel = maxFuelLbs - fuelBurn;
		if (maxLandingFuel > 0) {
			points.push({ type: 'maxFuelLanding', ...computePoint(maxLandingFuel, maxFuelArm) });
		}
	}

	if (minFuelLbs && currentFuelArm) {
		points.push({ type: 'minFuelTakeoff', ...computePoint(minFuelLbs, currentFuelArm) });
		const minLandingFuel = minFuelLbs - fuelBurn;
		if (minLandingFuel > 0) {
			points.push({ type: 'minFuelLanding', ...computePoint(minLandingFuel, currentFuelArm) });
		}
	}

	return points.length >= 2 ? points : null;
}
